/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { PCSettings } from '../../../../../core/entities';
import { InteractiveComponents } from '../../../../../core/enums';
import { ParentalControlServiceImpl } from '../../../../../core/impls';
import { ParentalControlService } from '../../../../../core/infrastructure';
import { ServicesClientImpl } from '../../../../../core/interactors';
import { clearGridGuideCache } from '../../../../guide/guide.reducers';
import {
    clearActiveInteractiveComponent,
    setActiveInteractiveComponent,
} from '../../../../interactivelayer/interactiveLayer.reducers';
import { showPCScreen } from '../../../../screens/screens.reducers';
import {
    ratingLimitsSetIsGhostModeEnabled,
    ratingLimitsSetSelectedOptionListAction,
    ratingLimitsSetVerticalPositionAction,
} from '../setratinglimits.actions';

export const onKeyBackSetRatingLimits = () => (dispatch: Function, getState: Function) => {
    const { selectedOptionList } = getState().setRatingLimits;
    const services = new ServicesClientImpl();
    const ratingOptionList = services
        .getPCRatingsOptions()
        .map((rating) => ({ [rating.ratingTypeId]: rating.options }));
    const unratedOptionList = services
        .getPCUnratedOptions()
        .map((rating) => ({ [rating.ratingTypeId]: rating.options }));

    const highestRatings = [...services.getPCHighestAllowedRatings()];
    const unratedContentAllowed = services.getPCUnratedContentAllowed();
    const ratingNameList = ratingOptionList.map((rating, index) => {
        if (Object.keys(rating)[0] === Object.keys(highestRatings[index])[0]) {
            return Object.values(rating)[0][Object.values(highestRatings[index])[0]];
        }
    });
    const unratedNameList = unratedOptionList.map((rating, index) => {
        if (Object.keys(rating)[0] === Object.keys(unratedContentAllowed[index])[0]) {
            return Object.values(rating)[0][Object.values(unratedContentAllowed[index])[0]];
        }
    });

    const selectedRatingNameList = ratingOptionList.map((rating, index) => {
        return Object.values(rating)[0][selectedOptionList[index]];
    });

    const selectedUnratedNameList = unratedOptionList.map((rating, index) => {
        return Object.values(rating)[0][selectedOptionList[index + unratedOptionList.length]];
    });
    for (let i = 0; i < ratingNameList.length; i += 1) {
        if (ratingNameList[i] !== selectedRatingNameList[i]) {
            highestRatings[i] = {
                [Object.keys(highestRatings[i])[0]]: String(
                    Object.values(ratingOptionList[i])[0].findIndex(
                        (rating) => rating === selectedRatingNameList[i],
                    ),
                ),
            };
            services.updatePCSetting(
                PCSettings.HEIGHEST_ALLOWED_RATINGS,
                JSON.stringify(highestRatings),
            );
        }
    }
    for (let i = 0; i < unratedNameList.length; i += 1) {
        if (unratedNameList[i] !== selectedUnratedNameList[i]) {
            unratedContentAllowed[i] = {
                [Object.keys(unratedContentAllowed[i])[0]]: String(
                    Object.values(unratedOptionList[i])[0].findIndex(
                        (rating) => rating === selectedUnratedNameList[i],
                    ),
                ),
            };
            services.updatePCSetting(
                PCSettings.UNRATED_CONTENT_ALLOWED,
                JSON.stringify(unratedContentAllowed),
            );
        }
    }

    dispatch(showPCScreen());
};

export const onKeyDownSetRatingLimits = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition, verticalLimit } = getState().setRatingLimits;
    const newVerticalPosition = verticalPosition + 1;
    if (newVerticalPosition < verticalLimit) {
        dispatch(ratingLimitsSetVerticalPositionAction(newVerticalPosition));
    }
};

export const onKeyLeftSetRatingLimits = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition, selectedOptionList, optionLimitList } = getState().setRatingLimits;
    if (optionLimitList[verticalPosition] > 1) {
        const updatedHorizontalPositions = selectedOptionList;
        updatedHorizontalPositions[verticalPosition] =
            (selectedOptionList[verticalPosition] - 1 + optionLimitList[verticalPosition]) %
            optionLimitList[verticalPosition];
        dispatch(ratingLimitsSetSelectedOptionListAction(updatedHorizontalPositions));
    }
};

export const onKeyRightSetRatingLimits = () => (dispatch: Function, getState: Function) => {
    const { selectedOptionList, optionLimitList, verticalPosition } = getState().setRatingLimits;
    if (optionLimitList[verticalPosition] > 1) {
        const updatedHorizontalPositions = selectedOptionList;
        updatedHorizontalPositions[verticalPosition] =
            (selectedOptionList[verticalPosition] + 1) % optionLimitList[verticalPosition];
        dispatch(ratingLimitsSetSelectedOptionListAction(updatedHorizontalPositions));
    }
};

export const onKeyUpSetRatingLimits = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition } = getState().setRatingLimits;
    if (verticalPosition > 0) {
        const newVerticalPosition = verticalPosition - 1;
        dispatch(ratingLimitsSetVerticalPositionAction(newVerticalPosition));
    }
};

export const onMouseOverSetRatingLimits = () => (dispatch: Function, getState: Function) => {
    const { setRatingLimits, inputFactory } = getState();
    const { verticalLimit } = setRatingLimits;
    const { value } = inputFactory;
    const newVerticalPosition = Number(value);
    if (value === InteractiveComponents.EAST || value === InteractiveComponents.WEST) {
        dispatch(setActiveInteractiveComponent(value));
    }

    if (Number.isNaN(newVerticalPosition)) return;

    if (newVerticalPosition < verticalLimit) {
        dispatch(ratingLimitsSetVerticalPositionAction(newVerticalPosition));
    }
    dispatch(ratingLimitsSetIsGhostModeEnabled(false));
};

export const onMouseOutSetRatingLimits = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    if (value === InteractiveComponents.EAST || value === InteractiveComponents.WEST) {
        dispatch(clearActiveInteractiveComponent());
    }
    dispatch(ratingLimitsSetIsGhostModeEnabled(true));
};

export const onMouseClickSetRatingLimits = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    switch (value) {
        case InteractiveComponents.WEST:
            dispatch(onKeyLeftSetRatingLimits());
            break;
        case InteractiveComponents.EAST:
            dispatch(onKeyRightSetRatingLimits());
            break;
        default:
    }
};
